import { useState } from 'react';
import { CalendarClock } from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Input } from '@/shared/components/ui/Input';
import { useAttendance } from '../hooks/useAnalytics';
import { PanelState } from './PanelState';
import { formatCount, todayISO } from '../constants';

function Kpi({ label, value }: { label: string; value: number | string }) {
  return (
    <Card className="p-4">
      <p className="text-display-sm font-semibold text-on-surface">{value}</p>
      <p className="mt-1 text-body-sm text-on-surface-variant">{label}</p>
    </Card>
  );
}

export function AttendancePanel({ enabled }: { enabled: boolean }) {
  const [logDate, setLogDate] = useState(todayISO());

  const { data, isLoading, isError } = useAttendance({ logDate, enabled });

  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            <CalendarClock className="h-4 w-4" /> Attendance
          </span>
        }
        subtitle="Present, absent, online and on-break counts for the selected day."
        action={
          <div className="w-44">
            <Input
              name="attendance-date"
              type="date"
              value={logDate}
              onChange={(e) => setLogDate(e.target.value || todayISO())}
            />
          </div>
        }
      />
      <CardBody>
        <PanelState
          isLoading={isLoading}
          isError={isError}
          isEmpty={!!data?.empty}
          emptyTitle="No attendance data"
          emptyDescription="No roster or time logs are available for the selected day and scope."
          emptyIcon={<CalendarClock className="h-6 w-6" />}
        >
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
            <Kpi label="Present" value={formatCount(data?.present ?? 0)} />
            <Kpi label="Absent" value={formatCount(data?.absent ?? 0)} />
            <Kpi label="Online" value={formatCount(data?.online ?? 0)} />
            <Kpi label="On break" value={formatCount(data?.onBreak ?? 0)} />
            <Kpi label="Total hours" value={`${(data?.totalHours ?? 0).toFixed(1)}h`} />
          </div>
        </PanelState>
      </CardBody>
    </Card>
  );
}
